import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Lock, ArrowRight } from "@phosphor-icons/react";

/**
 * Tela "Assinatura pendente" — exibida quando login retorna 402.
 * Visual premium pra não parecer página de cobrança.
 */
const STATUS_COPY = {
  pending_payment: {
    eyebrow: "Quase lá",
    title: "Falta confirmar sua assinatura",
    body: "Sua conta já está cadastrada — basta concluir o pagamento pra liberar o painel.",
    cta: "Concluir assinatura",
  },
  past_due: {
    eyebrow: "Atenção",
    title: "Sua assinatura precisa ser regularizada",
    body: "Não conseguimos cobrar a última mensalidade. Atualize seu cartão pra continuar usando o AgentUS — seus dados estão preservados.",
    cta: "Regularizar assinatura",
  },
  cancelled: {
    eyebrow: "Bem-vindo de volta",
    title: "Reative sua conta no AgentUS",
    body: "Sua assinatura está cancelada. Tudo que você criou continua aqui — é só reativar pra voltar a usar.",
    cta: "Reativar assinatura",
  },
};

export default function Regularize() {
  const nav = useNavigate();
  const [intent, setIntent] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const raw = sessionStorage.getItem("agentus_checkout_intent");
    if (!raw) {
      nav("/login");
      return;
    }
    setIntent(JSON.parse(raw));
  }, [nav]);

  if (!intent) return null;
  const copy = STATUS_COPY[intent.tenant_status] || STATUS_COPY.pending_payment;

  const proceed = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const { data } = await api.post("/payments/subscribe", {
        registration_token: intent.registration_token,
        origin_url: window.location.origin,
        // v0.96 — past_due regulariza no mesmo plano que tinha antes.
        // Se intent não trouxer plan_code (clientes antigos), backend
        // usa default "base" — comportamento histórico preservado.
        plan_code: intent.plan_code || "base",
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Não consegui abrir o pagamento. Tente novamente.");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 flex items-center justify-center p-4" data-testid="regularize-page">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl shadow-slate-200/60 border border-slate-200/80 p-8">
        <div className="w-12 h-12 rounded-full bg-slate-900 text-white mx-auto flex items-center justify-center mb-5">
          <Lock size={20} weight="bold" />
        </div>

        <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400 mb-2 text-center">{copy.eyebrow}</div>
        <h1 className="text-[22px] font-semibold tracking-tight text-slate-900 mb-3 text-center leading-snug">
          {copy.title}
        </h1>
        <p className="text-[13px] text-slate-600 leading-relaxed text-center mb-6">
          {copy.body}
        </p>

        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-5 text-center">
          <div className="text-[11px] text-slate-500 mb-0.5">Conta</div>
          <div className="text-[13px] text-slate-900 font-medium">{intent.tenant_name || intent.email}</div>
          {intent.tenant_name && intent.email && (
            <div className="text-[11px] text-slate-500 mt-0.5">{intent.email}</div>
          )}
        </div>

        <button
          onClick={proceed}
          disabled={loading}
          data-testid="regularize-cta"
          className="w-full bg-slate-900 text-white text-[14px] font-medium py-3 rounded-xl hover:bg-slate-700 transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {loading ? (
            <>
              <span className="w-3.5 h-3.5 rounded-full border-2 border-white/25 border-t-white animate-spin" />
              Abrindo pagamento…
            </>
          ) : (
            <>
              {copy.cta}
              <ArrowRight size={14} weight="bold" />
            </>
          )}
        </button>

        <button
          onClick={() => { sessionStorage.removeItem("agentus_checkout_intent"); nav("/login"); }}
          className="w-full mt-2 text-[12.5px] text-slate-500 hover:text-slate-900 py-2"
          data-testid="regularize-back-btn"
        >
          Voltar para o login
        </button>
      </div>
    </div>
  );
}
